import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import { STATUS_COLOR_INFO } from '@/common/utils/statuses';

export const UPDATES_CLUSTER_STATUSES = {
  AVAILABLE: 'available',
  CANCELLING: 'cancelling',
  CHECK_FAILED: 'check_failed',
  CHECKING: 'checking',
  CLUSTER_DEGRADED: 'cluster_degraded',
  DOWNLOADING: 'downloading',
  DOWNLOAD_COMPLETED: 'download_completed',
  FAILED: 'failed',
  MAINTENANCE_FAILED: 'maintenance_failed',
  MAJOR_UPDATE: 'major_update',
  NOCLUSTER: 'nocluster',
  NONE: 'none',
  PAUSING: 'pausing',
  PAUSED: 'paused',
  UPDATING: 'updating',
  UPGRADE_AVAILABLE: 'upgrade_available',
  UPTODATE: 'uptodate',
};

export const UPDATES_CLUSTER_ERROR_STATUSES = [
  UPDATES_CLUSTER_STATUSES.FAILED,
  UPDATES_CLUSTER_STATUSES.CHECK_FAILED,
  UPDATES_CLUSTER_STATUSES.CLUSTER_DEGRADED,
  UPDATES_CLUSTER_STATUSES.MAINTENANCE_FAILED
];

export const UPDATES_CLUSTER_CANCELLING_STATUSES = [
  UPDATES_CLUSTER_STATUSES.MAINTENANCE_FAILED,
];

export const UPDATES_CLUSTER_PROGRESS_STATUSES = [
  UPDATES_CLUSTER_STATUSES.UPDATING
];

export const UPDATES_CLUSTER_NOT_READY_STATUSES = [
  UPDATES_CLUSTER_STATUSES.CLUSTER_DEGRADED,
  UPDATES_CLUSTER_STATUSES.NOCLUSTER
];

export const UPDATES_CLUSTER_NOT_CHECKED_STATUSES = [
  UPDATES_CLUSTER_STATUSES.NONE,
  UPDATES_CLUSTER_STATUSES.CHECK_FAILED,
  UPDATES_CLUSTER_STATUSES.UPGRADE_AVAILABLE
];

export const UPDATES_CLUSTER_DOWNLOAD_STATUSES = [
  UPDATES_CLUSTER_STATUSES.AVAILABLE,
  UPDATES_CLUSTER_STATUSES.UPGRADE_AVAILABLE
];

export const UPDATES_NODE_STATUSES = {
  AVAILABLE: 'available',
  CHECK_FAILED: 'check_failed',
  CHECKING: 'checking',
  CLEANING_UP: 'cleaning_up',
  DOWNLOAD_COMPLETED: 'download_completed',
  DOWNLOAD_FAILED: 'download_failed',
  DOWNLOADING: 'downloading',
  ENTERING_MAINTENANCE: 'entering_maintenance',
  FAILED: 'failed',
  LEAVING_MAINTENANCE: 'leaving_maintenance',
  MAINTENANCE_ENTER_FAILED: 'maintenance_failed',
  UPDATE_KERNEL_FAILED: 'update_kernel_failed',
  MAINTENANCE_EXIT_FAILED: 'maintenance_exit_failed',
  NONE: 'none',
  PAUSED: 'paused',
  PAUSING: 'pausing',
  PENDING: 'pending',
  REBOOTING: 'rebooting',
  SETTING_UP: 'setting_up',
  UPDATING: 'updating',
  UPDATING_KERNEL: 'updating_kernel',
  UPTODATE: 'uptodate',

  // Absent statuses from [Updates v3 api schema] (https://git.acronis.com/projects/STRG/repos/pstorage-ui-backend-schema/browse/schema/components/schemas/SoftwareUpdatesV3.yaml)
  FAILED_COMPUTE: 'failed_compute', // TODO Change to UPDATES_SERVICE_STATUSES.FAILED
  FAILED_ISCSI: 'failed_iscsi',
  FAILED_NFS: 'failed_nfs',
  FAILED_S3: 'failed_s3',
  FAILED_STORAGE: 'failed_storage',
};

export const UPDATES_NODE_STATUSES_MAPPING = computedLocale(() => ({
  // Download statuses
  [UPDATES_NODE_STATUSES.AVAILABLE]: {
    icon: 'STATUS_ICON_INFO',
    label: i18n.t('Available')
  },
  [UPDATES_NODE_STATUSES.CHECK_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to check for updates')
  },
  [UPDATES_NODE_STATUSES.CHECKING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Checking...')
  },
  [UPDATES_NODE_STATUSES.CLEANING_UP]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Cleaning up...')
  },
  [UPDATES_NODE_STATUSES.DOWNLOAD_COMPLETED]: {
    icon: 'STATUS_ICON_INSTALL_READY',
    label: i18n.t('Ready to install')
  },
  [UPDATES_NODE_STATUSES.DOWNLOAD_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to download updates')
  },
  [UPDATES_NODE_STATUSES.DOWNLOADING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Downloading [updates status]')
  },
  [UPDATES_NODE_STATUSES.ENTERING_MAINTENANCE]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Entering maintenance...')
  },
  [UPDATES_NODE_STATUSES.FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to install')
  },
  [UPDATES_NODE_STATUSES.FAILED_COMPUTE]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter compute maintenance')
  },
  [UPDATES_NODE_STATUSES.FAILED_STORAGE]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter storage maintenance')
  },
  [UPDATES_NODE_STATUSES.FAILED_S3]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter S3 maintenance')
  },
  [UPDATES_NODE_STATUSES.FAILED_NFS]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter NFS maintenance')
  },
  [UPDATES_NODE_STATUSES.FAILED_ISCSI]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter iSCSI maintenance')
  },
  [UPDATES_NODE_STATUSES.LEAVING_MAINTENANCE]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Exiting maintenance...')
  },
  [UPDATES_NODE_STATUSES.MAINTENANCE_ENTER_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to enter maintenance')
  },
  [UPDATES_NODE_STATUSES.MAINTENANCE_EXIT_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to exit maintenance')
  },
  [UPDATES_NODE_STATUSES.UPDATE_KERNEL_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to update kernel')
  },
  [UPDATES_NODE_STATUSES.NONE]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Unknown')
  },
  [UPDATES_NODE_STATUSES.PAUSED]: {
    icon: 'STATE_ICON_PAUSE',
    label: i18n.t('Paused')
  },
  [UPDATES_NODE_STATUSES.PAUSING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Pausing')
  },
  [UPDATES_NODE_STATUSES.PENDING]: {
    icon: 'STATUS_ICON_CLOCK',
    label: i18n.t('Pending update')
  },
  [UPDATES_NODE_STATUSES.REBOOTING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Rebooting...')
  },
  [UPDATES_NODE_STATUSES.SETTING_UP]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Configuring...')
  },
  [UPDATES_NODE_STATUSES.UPDATING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Installing...')
  },
  [UPDATES_NODE_STATUSES.UPDATING_KERNEL]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Updating kernel...')
  },
  [UPDATES_NODE_STATUSES.UPTODATE]: {
    icon: 'STATUS_ICON_CHECK_O',
    label: i18n.t('Up to date')
  }
}));

export const UPDATES_CLUSTER_STATUSES_MAPPING = computedLocale(() => ({
  [UPDATES_CLUSTER_STATUSES.AVAILABLE]: {
    icon: 'STATUS_ICON_INFO',
    label: i18n.t('Updates are available'),
    color: STATUS_COLOR_INFO
  },
  [UPDATES_CLUSTER_STATUSES.CANCELLING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Cancelling...')
  },
  [UPDATES_CLUSTER_STATUSES.CHECKING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Checking for updates...')
  },
  [UPDATES_CLUSTER_STATUSES.CHECK_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Failed to check for updates')
  },
  [UPDATES_CLUSTER_STATUSES.CLUSTER_DEGRADED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Cluster degraded')
  },
  [UPDATES_CLUSTER_STATUSES.DOWNLOADING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Downloading updates...')
  },
  [UPDATES_CLUSTER_STATUSES.DOWNLOAD_COMPLETED]: {
    icon: 'STATUS_ICON_INFO',
    label: i18n.t('Updates are ready to install. Select nodes to install the updates.'),
    color: STATUS_COLOR_INFO
  },
  [UPDATES_CLUSTER_STATUSES.FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Update failed to install')
  },
  [UPDATES_CLUSTER_STATUSES.MAJOR_UPDATE]: {
    icon: 'STATUS_ICON_INFO',
    label: i18n.t('Updates include a new major version and are ready to install. Select nodes to install the updates.'),
    color: STATUS_COLOR_INFO
  },
  [UPDATES_CLUSTER_STATUSES.MAINTENANCE_FAILED]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Maintenance failed')
  },
  [UPDATES_CLUSTER_STATUSES.NOCLUSTER]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Cluster is not found')
  },
  [UPDATES_CLUSTER_STATUSES.NONE]: {
    icon: 'STATUS_ICON_TIMES_O',
    label: i18n.t('Update status unknown')
  },
  [UPDATES_CLUSTER_STATUSES.PAUSING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Pausing update...')
  },
  [UPDATES_CLUSTER_STATUSES.PAUSED]: {
    icon: 'STATE_ICON_PAUSE',
    label: i18n.t('Paused')
  },
  [UPDATES_CLUSTER_STATUSES.UPDATING]: {
    icon: 'STATUS_ICON_LOADING',
    label: i18n.t('Installing updates...')
  },
  [UPDATES_CLUSTER_STATUSES.UPGRADE_AVAILABLE]: {
    icon: 'STATUS_ICON_INFO',
    label: i18n.t('Upgrade is available'),
    color: STATUS_COLOR_INFO
  },
  [UPDATES_CLUSTER_STATUSES.UPTODATE]: {
    icon: 'STATUS_ICON_CHECK_O',
    label: i18n.t('Up to date')
  },
}));

export const UPDATES_SERVICE_STATUSES = {
  AVAILABLE: 'available',
  UPTODATE: 'uptodate',
  FAILED: 'failed',
  UPDATING: 'updating',
};

export const UPDATES_MAINTENANCE_MODES = {
  SKIP: 'skip',
  STOP: 'stop',
  FORCE: 'force',
  NO_MAINTENANCE: 'no_maintenance'
};

export const UPDATES_COMPUTE_MAINTENANCE_MODES = {
  IGNORE: 'ignore',
  IGNORE_EXT: 'ignore_ext',
  STRICT: 'strict',
};

export const UPDATES_ON_FAIL_OPTIONS = {
  STOP: 'stop',
  SKIP: 'skip',
  FORCE: 'force',
};

export const UPDATES_CANCEL_MODES = {
  LEAVE: 'leave',
  LEAVE_IGNORE: 'leave_ignore',
  HOLD: 'hold'
};

export enum UPDATES_NODE_TYPES {
  PRIMARY_NODE = 'primary',
  CLUSTER_NODE = 'cluster',
  MANAGEMENT_NODE = 'management',
  SERVICE = 'service'
}

export enum SERVICES_NAMES {
  CONTROL_PLANE = 'CONTROL_PLANE'
}

export const UPDATES_NODE_STATES = {
  PENDING: 'pending',
  SETTING_UP: 'setting_up',
  ENTERING_MAINTENANCE: 'entering_maintenance',
  UPDATING: 'updating',
  REBOOTING: 'rebooting',
  LEAVING_MAINTENANCE: 'leaving_maintenance',
  CLEANING_UP: 'cleaning_up',
  UPTODATE: 'uptodate',
};

export const UPDATES_OPERATIONS = {
  CANCEL: 'CANCEL',
  CANCEL_UPDATES: 'CANCEL_UPDATES',
  CHECK_ELIGIBILITY: 'CHECK_ELIGIBILITY',
  CHECK_UPDATES: 'CHECK_UPDATES',
  DOWNLOAD: 'DOWNLOAD',
  PAUSE: 'PAUSE',
  START: 'START',
  RESUME: 'RESUME',
  UPDATES_LOADING: 'UPDATES_LOADING',
};

export const UPDATES_TRANSITIONS = {
  initial: UPDATES_NODE_STATUSES.NONE,
  states: {
    [UPDATES_CLUSTER_STATUSES.NONE]: {
      on: {
        [UPDATES_OPERATIONS.CHECK_UPDATES]: UPDATES_CLUSTER_STATUSES.CHECKING
      }
    },
    [UPDATES_CLUSTER_STATUSES.UPDATING]: {
      on: {
        [UPDATES_OPERATIONS.PAUSE]: UPDATES_CLUSTER_STATUSES.PAUSING,
        [UPDATES_OPERATIONS.CANCEL_UPDATES]: UPDATES_CLUSTER_STATUSES.CANCELLING
      }
    },
    [UPDATES_CLUSTER_STATUSES.PAUSED]: {
      on: {
        [UPDATES_OPERATIONS.RESUME]: UPDATES_CLUSTER_STATUSES.UPDATING
      }
    },
  },
  operations: {
    [UPDATES_OPERATIONS.CANCEL]: UPDATES_CLUSTER_STATUSES.CANCELLING,
    [UPDATES_OPERATIONS.CHECK_ELIGIBILITY]: UPDATES_CLUSTER_STATUSES.CHECKING,
    [UPDATES_OPERATIONS.CHECK_UPDATES]: UPDATES_CLUSTER_STATUSES.CHECKING,
    [UPDATES_OPERATIONS.DOWNLOAD]: UPDATES_CLUSTER_STATUSES.DOWNLOADING,
    [UPDATES_OPERATIONS.START]: UPDATES_CLUSTER_STATUSES.UPDATING,
    [UPDATES_OPERATIONS.PAUSE]: UPDATES_CLUSTER_STATUSES.PAUSING,
    [UPDATES_OPERATIONS.RESUME]: UPDATES_CLUSTER_STATUSES.UPDATING,
    [UPDATES_OPERATIONS.CANCEL_UPDATES]: UPDATES_CLUSTER_STATUSES.CANCELLING,
    [UPDATES_OPERATIONS.UPDATES_LOADING]: UPDATES_CLUSTER_STATUSES.UPDATING
  }
};

export const UPDATE_IMPACT = {
  NONE: 'none',
  MAINTENANCE: 'maintenance_required',
  REBOOT: 'reboot_required'
};

export const UPDATE_IMPACT_STATUSES_MAPPING = computedLocale(() => ({
  [UPDATE_IMPACT.NONE]: i18n.t('No impact'),
  [UPDATE_IMPACT.MAINTENANCE]: i18n.t('Maintenance required'),
  [UPDATE_IMPACT.REBOOT]: i18n.t('Reboot required'),
}));

export const UPDATE_IMPACT_STATUSES_DESCRIPTION = computedLocale(() => ({
  [UPDATE_IMPACT.NONE]: i18n.t('When an update has no impact on the cluster operation, all services are available during the update.'),
  [UPDATE_IMPACT.MAINTENANCE]: i18n.t('When maintenance is required for an update, all services enter the maintenance mode during the update, if needed, but updated nodes do not require a reboot.'),
  [UPDATE_IMPACT.REBOOT]: i18n.t('When a reboot is required for an update, all services enter the maintenance mode during the update, if needed, and then updated nodes are rebooted automatically, one at a time.'),
}));

export const PROGRESSIVE_UPDATES_OPERATIONS = [
  UPDATES_CLUSTER_STATUSES.CANCELLING,
  UPDATES_CLUSTER_STATUSES.CHECKING,
  UPDATES_CLUSTER_STATUSES.DOWNLOADING,
  UPDATES_CLUSTER_STATUSES.PAUSING,
  UPDATES_CLUSTER_STATUSES.UPDATING
];

export const UPDATE_DIALOGS = {
  FAIL_ELIGIBILITY_DIALOG: 'FAIL_ELIGIBILITY_DIALOG',
  FAIL_UPDATE_DIALOG: 'FAIL_UPDATE_DIALOG',
  RELEASE_NOTES_DIALOG: 'RELEASE_NOTES_DIALOG',
  INSTALL_UPDATES_DIALOG: 'INSTALL_UPDATES_DIALOG',
  FORCE_UPDATE_DIALOG: 'FORCE_UPDATE_DIALOG',
  SELECT_VERSION: 'SELECT_VERSION'
};

export enum ELIGIBILITY_CHECK_IDS{
  CHUNKS_REBALANCING_RATE= 'chunks_rebalancing_rate',
  CLUSTER_HAS_RELEASING_NODES= 'cluster_has_releasing_nodes',
  CLUSTER_LOW_SPACE= 'cluster_low_space',
  CLUSTER_UNHEALTHY= 'cluster_unhealthy',
  INCOMPATIBLE_HARDWARE= 'incompatible_hardware',
  NOT_ENOUGH_SPACE_ON_AGENTS= 'not_enough_space_on_agents',
  NOT_ENOUGH_SPACE_ON_MN= 'not_enough_space_on_mn',
  POSTGRES_NOT_RUNNING= 'postgres_not_running',
  REQUEST_ACCEPT_EULA= 'request_accept_eula',
  SERVER_WITH_PCI_DEVICES= 'server_with_pci_devices',
  SERVER_WITH_VGPU= 'server_with_vgpu',
  SHAMAN= 'shaman',
  TGTD= 'tgtd',
  TOO_MANY_PENDING_CHUNKS= 'too_many_pending_chunks',
  UNKNOWN= 'unknown',
}

export const ELIGIBILITY_CHECK_TITLES = computedLocale<Record<ELIGIBILITY_CHECK_IDS, string>>(() => ({
  [ELIGIBILITY_CHECK_IDS.CHUNKS_REBALANCING_RATE]: i18n.t('Chunks rebalancing problems'),
  [ELIGIBILITY_CHECK_IDS.CLUSTER_HAS_RELEASING_NODES]: i18n.t('Some nodes are being released'),
  [ELIGIBILITY_CHECK_IDS.CLUSTER_LOW_SPACE]: i18n.t('Not enough storage space'),
  [ELIGIBILITY_CHECK_IDS.CLUSTER_UNHEALTHY]: i18n.t('Cluster degraded'),
  [ELIGIBILITY_CHECK_IDS.INCOMPATIBLE_HARDWARE]: i18n.t('Unsupported hardware detected'),
  [ELIGIBILITY_CHECK_IDS.NOT_ENOUGH_SPACE_ON_AGENTS]: i18n.t('Not enough space on agent nodes'),
  [ELIGIBILITY_CHECK_IDS.NOT_ENOUGH_SPACE_ON_MN]: i18n.t('Not enough space on management node'),
  [ELIGIBILITY_CHECK_IDS.POSTGRES_NOT_RUNNING]: i18n.t('PostgreSQL database service is down'),
  [ELIGIBILITY_CHECK_IDS.REQUEST_ACCEPT_EULA]: i18n.t('EULA is not accepted'),
  [ELIGIBILITY_CHECK_IDS.SERVER_WITH_PCI_DEVICES]: i18n.t('Virtual machines with PCI devices detected'),
  [ELIGIBILITY_CHECK_IDS.SERVER_WITH_VGPU]: i18n.t('Virtual machines with vGPU detected'),
  [ELIGIBILITY_CHECK_IDS.SHAMAN]: i18n.t('Shaman service is down'),
  [ELIGIBILITY_CHECK_IDS.TGTD]: i18n.t('Legacy iSCSI targets detected'),
  [ELIGIBILITY_CHECK_IDS.TOO_MANY_PENDING_CHUNKS]: i18n.t('Too many pending chunks'),
  [ELIGIBILITY_CHECK_IDS.UNKNOWN]: i18n.t('Other problems'),
}));

export const UPGRADE_VERSION_MESSAGES = computedLocale(() => ({
  '6.x.version': ({ product }) => i18n.t('With this version of {product}, we are introducing Linux kernel 5.x and an upgrade of core packages.', { product })
}));
